// src/components/CategoryNewsSection.js
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import './CategoryNewsSection.css';
import API_BASE_URL from '../utils/apiConfig';
import { injectMixedContent } from '../utils/adInjector';
import AdCard from './AdCard';

// Custom news card designed specifically to match your side-by-side requirement
const CategoryNewsCard = ({ newsItem }) => {
  const {
    title,
    news_title,
    image_url,
    news_image,
    source_name,
    source,
    pubDate,
    published_at,
    link,
    news_link
  } = newsItem;

  const displayTitle = title || news_title || "No Title";
  const displayImage = image_url || news_image || "https://via.placeholder.com/150";
  const displaySource = source_name || source || "Unknown Source";
  const displayDate = pubDate || published_at || "";
  const displayLink = link || news_link || "#";

  return (
    <div className="custom-category-card">
      {/* Side-by-side Top row (Image Left, News Right) */}
      <div className="card-top-row">
        <div className="card-image-container">
          <img src={displayImage} alt={displayTitle} className="card-image" />
        </div>
        <div className="card-info-container">
          <h3 className="card-title">{displayTitle}</h3>
          <div className="card-meta">
            <span className="card-source">{displaySource}</span>
            {displayDate && <span className="card-date"> | {displayDate.substring(0, 10)}</span>}
          </div>
        </div>
      </div>
      
      {/* Full width Read Full Story Tab below the top row */}
      <a href={displayLink} target="_blank" rel="noopener noreferrer" className="read-full-story-bar">
        Read Full Story
      </a>
    </div>
  );
};

const CategoryNewsSection = ({ categories }) => {
  const [selectedCategory, setSelectedCategory] = useState(categories[0]?.value || '');
  const [categoryNews, setCategoryNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mixedContent, setMixedContent] = useState([]);
  
  const tabRefs = useRef({});

  useEffect(() => {
    const fetchCategoryNews = async () => {
      if (!selectedCategory) return;
      setLoading(true);
      setError('');
      try {
        const [newsResponse, adsResponse] = await Promise.all([
            axios.get(`${API_BASE_URL}/category-news/${selectedCategory}`),
            axios.get(`${API_BASE_URL}/ads`)
        ]);
        const newsData = newsResponse.data || [];
        setCategoryNews(newsData);
        setMixedContent(injectMixedContent(newsData, adsResponse.data, 3)); 
      } catch (err) {
        console.error(`Error fetching ${selectedCategory} news:`, err);
        setError(`Failed to load ${selectedCategory} news.`);
        setCategoryNews([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryNews();
  }, [selectedCategory]);

  // Handle active category tab auto-centering
  useEffect(() => {
    if (selectedCategory && tabRefs.current[selectedCategory]) {
      tabRefs.current[selectedCategory].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [selectedCategory]);

  return (
    <div className="category-news-section-container">
      {/* Horizontally scrolling pill-shaped category tabs */}
      <div className="category-scroll-tabs">
        {categories.map((cat) => (
          <button
            key={cat.value}
            ref={(el) => (tabRefs.current[cat.value] = el)}
            className={`category-pill-tab ${selectedCategory === cat.value ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.value)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="category-news-content">
        {loading && <p className="loading-message">Loading news...</p>}
        {error && <p className="error-message">{error}</p>}
        
        <div className="category-news-grid">
          {!loading && !error && categoryNews.length === 0 && (
            <p className="no-news-message">No news found for this category.</p>
          )}
          {mixedContent.map((item) => {
            if (item.isAd) {
              return <AdCard key={item.adId} adData={item} />;
            } else {
              return <CategoryNewsCard key={item.article_id} newsItem={item} />;
            }
          })}
        </div>
      </div>
    </div>
  );
};

export default CategoryNewsSection;